import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
} from "reactstrap";
import { US, CO } from "country-flag-icons/react/3x2";
import Context from "contextAPI/UserContext";
import useUser from "hooks/useUser";
import swal from "sweetalert";
import { showAlertServiceError } from "utils/alerts";
import { HttpStatus } from "utils/enums";

const ButtonChangeLanguage = ({isMobile}) => {
  const { user, setLanguage } = useContext(Context);
  const { i18n, t } = useTranslation();
  const { updateUserLanguage } = useUser();

  const languages = [
    { code: "en", label: "English", Flag: US },
    { code: "es", label: "Español", Flag: CO },
  ];

  let toggleButton = null;


  if (isMobile === true) {
    toggleButton = (
      <DropdownToggle className="p-0 mr-2" color="link">
        <div className="d-flex">
          <div className="language-icon">
            {(() => {
              const CurrentFlag =
                languages.find((l) => l.code === i18n.language)?.Flag || CO;
              return (
                <CurrentFlag className=" ml-3 opacity-8" style={{ width: 15, height: 15 }} />
              );
            })()}
          </div>
            <span className="ml-2 font-weight-bold">
              {t("editUsers.selectLanguage")}
            </span>
        </div>
      </DropdownToggle>

    );
  } else {
    toggleButton = (
      <DropdownToggle className="p-0 mr-2" color="link">
        <div className="icon-wrapper icon-wrapper-alt rounded-circle">
          <div className="icon-wrapper-bg bg-focus" />
          <div className="language-icon">
            {(() => {
              const CurrentFlag =
                languages.find((l) => l.code === i18n.language)?.Flag || CO;
              return (
                <CurrentFlag className="ml-2 opacity-8" style={{ width: 35, height: 35 }} />
              );
            })()}
          </div>
        </div>
      </DropdownToggle>
    );
  }

  /**
   * Handles the language change action for the current user.
   * This function updates the application language locally and persists
   * the selected language for the authenticated user via the updateUser service.
   * If the selected language is the same as the current one, the function exits early.
   * On successful update (HTTP 202), a success modal is displayed.
   * On failure, a generic service error alert is shown.
   * Unauthorized errors (HTTP 401) are silently ignored.
   * @param {string} lang - The language code to set (e.g., "en", "es").
   */
  const handleChangeLanguage = (lang) => {
    if (lang === i18n.language) return;

    setLanguage(lang);

    updateUserLanguage({ language: lang }, user.uuid)
      .then((response) => {
        if (response.status === 202) {
          swal({
            title: t("modal.DoneError.header"),
            text: t("editUsers.language"),
            icon: "success",
            button: t("modal.Done.footerButton"),
          }).then(() => {
            window.location.reload();
          });
        } else {
          showAlertServiceError();
        }
      })
      .catch((error) => {
        if (error.response?.status === HttpStatus.UNAUTHORIZED) return;
      });
  };

 

  return (
    <UncontrolledDropdown>
      {toggleButton}
      <DropdownMenu right className="rm-pointers">
        <DropdownItem header>{t("editUsers.selectLanguage")}</DropdownItem>

        {languages.map(({ code, label, Flag }) => {
          const isActive = i18n.language === code;

          return (
            <DropdownItem
              key={code}
              disabled={isActive}
              onClick={() => handleChangeLanguage(code)}
              className="d-flex align-items-center justify-content-between"
              style={{
                cursor: isActive ? "default" : "pointer",
                opacity: isActive ? 0.7 : 1,
              }}
            >
              <div className="d-flex align-items-center">
                <Flag style={{ width: 24, height: 24, marginRight: 10 }} />
                {label}
              </div>

              {isActive && (
                <Badge color="success" pill>
                  ✓
                </Badge>
              )}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

ButtonChangeLanguage.propTypes = {
  isMobile: PropTypes.bool.isRequired
};

export default ButtonChangeLanguage;
